// The device the address names — `?serial=` on a page that understands it
// (`facets.ts`) — read once for everything on screen that needs it (#1311).
// The context bar names its chip and lands the scope from it (`FacetBar.tsx`),
// and the page's head and detail read its catalogue model and address from it
// (`useDeviceModel`). Before this each made `GET /devices/{serial}` for
// itself, so one page view on Operate › Devices, Design › Physical › Equipment
// or Design › Wired › Ports cost the cell two per-device reads (§9.5).
//
// It is not a cache of server state in general (that is #198's question,
// ruled by ADR-0419 `1d`, and #1862 holds its re-read). It is one shared
// reader for one shape of data, in `estate.tsx`'s pattern: the first reader
// on screen starts the read, every reader holds the same answer, and it is
// read again at named moments — the page's reload after an act on the device
// (a reboot, an upgrade, a factory reset) and after an edit to it (its name,
// its site, unclaiming it). What drops it: the address naming another serial
// or none, and another person signing in. Nothing holds it across a
// navigation to another device, and nothing trusts it past those moments —
// it is one page view's answer, not a store, and ADR-0234 `1c`'s per-device
// read stays live.
//
// A reader asking about any other serial — a link's far end (`Links.tsx`) —
// is not this device and reads for itself; `useAddressedDevice` answers it
// `undefined`.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../api/client";
import { SKIP, useQuery, type QueryState } from "../api/hooks";
import { facetsOf, useFacets } from "./facets";
import { useSession } from "./session";

/** The one place the browser asks for a device by serial, for this reader and
 * for `useDeviceModel`'s read of a serial the address does not name. */
export const readDevice = (serial: string) => api.GET("/devices/{serial}", { params: { path: { serial } } });

/** What `GET /devices/{serial}` answers. */
export type DeviceRead = NonNullable<Awaited<ReturnType<typeof readDevice>>["data"]>;

/** The shared read, with the moment it is read again. */
export type AddressedDevice = QueryState<DeviceRead> & { reload(): void };

interface Reader {
  /** The serial the address names on a page that understands it. */
  serial?: string;
  state: AddressedDevice;
  /** Says a reader of `serial` is on screen; the first one starts the read. */
  ensure(serial: string): void;
}

const Ctx = createContext<Reader | undefined>(undefined);

export function DeviceProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const who = me?.user.id;
  const { pathname } = useLocation();
  const { facets } = useFacets();
  // The same test the context bar applies: a serial pasted onto a page that
  // does not read it names nothing here either.
  const serial = facetsOf(pathname).includes("serial") ? facets.serial : undefined;
  // Which serial a reader on screen has asked for. Until one has, nothing is
  // read: a page with the facet and no reader of it costs no request.
  const [wanted, setWanted] = useState<string>();
  // And nothing is read for nobody: before the session has answered there is
  // no person to read as, and a read then would be read again the moment one
  // arrives, which is the second request this module exists to remove.
  const asked = serial !== undefined && wanted === serial && who !== undefined;
  // `useQuery` is the read: a change of key — another serial, none, another
  // person — drops the answer rather than showing it under the new one, and
  // its `reload` keeps the answer on screen while it is read again.
  const query = useQuery(() => asked ? readDevice(serial) : SKIP, [serial, asked, who]);
  const ensure = useCallback((s: string) => setWanted(s), []);
  // Asked for and not yet started is loading, not idle: a reader on screen
  // has a read coming, and its first render says so rather than "nothing".
  const { reload } = query;
  const state = useMemo<AddressedDevice>(() => asked ? query : { status: "loading", reload }, [asked, query, reload]);
  const value = useMemo<Reader>(() => ({ serial, state, ensure }), [serial, state, ensure]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The shared read of `serial`, when it is the device the address names; the
 * first caller on screen starts it. `undefined` for any other serial, or none —
 * that caller reads for itself, or has nothing to read. */
export function useAddressedDevice(serial: string | undefined): AddressedDevice | undefined {
  const r = useContext(Ctx);
  if (!r) throw new Error("useAddressedDevice outside DeviceProvider");
  const mine = serial !== undefined && serial === r.serial;
  const { ensure } = r;
  useEffect(() => { if (mine) ensure(serial); }, [mine, serial, ensure]);
  return mine ? r.state : undefined;
}
